// Connectivity diagnostics page (Phase 2 Slice C).

import { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext.js';
import { health, type ConnectivityEntry, type ConnectivityResponse } from '../../lib/api.js';

function silentLabel(min: number): string {
  if (min < 0) return 'never';
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  if (min < 60 * 24) return `${Math.round(min / 60)}h ago`;
  return `${Math.round(min / 60 / 24)}d ago`;
}

function signalLabel(e: ConnectivityEntry): string {
  if (e.lastSignal === null) return '—';
  return `${e.lastSignal}${e.lastSignalUnit ? ` ${e.lastSignalUnit}` : ''}`;
}

export default function ConnectivityPage() {
  const { currentHomeId } = useAuth();
  const [data, setData] = useState<ConnectivityResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentHomeId) return;
    let cancelled = false;
    setLoading(true);
    health
      .connectivity(currentHomeId)
      .then((res) => {
        if (cancelled) return;
        setData(res);
        setError(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Could not load connectivity data.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [currentHomeId]);

  if (!currentHomeId) {
    return <p style={{ color: 'var(--ac-text-muted)' }}>Select a home first.</p>;
  }

  if (loading) {
    return <p style={{ color: 'var(--ac-text-muted)' }}>Loading connectivity…</p>;
  }

  if (error) {
    return (
      <div role="alert" style={{ color: 'var(--ac-danger, crimson)' }}>
        {error}
      </div>
    );
  }

  if (!data || data.flakiestEntities.length === 0) {
    return (
      <section aria-labelledby="connectivity-empty">
        <h1 id="connectivity-empty" className="text-xl font-semibold mb-2">
          Connectivity
        </h1>
        <p style={{ color: 'var(--ac-text-muted)' }}>
          No devices found in this home yet. Pair a bridge and stream some state, then come back.
        </p>
      </section>
    );
  }

  return (
    <section aria-labelledby="connectivity-heading">
      <header className="mb-4 flex items-baseline justify-between">
        <h1 id="connectivity-heading" className="text-xl font-semibold">
          Connectivity
        </h1>
        <p style={{ color: 'var(--ac-text-muted)' }}>
          showing {data.flakiestEntities.length} flakiest
        </p>
      </header>

      <ul
        className="mb-4 flex flex-wrap gap-3 text-sm"
        style={{ color: 'var(--ac-text-muted)' }}
        aria-label="Devices by protocol"
      >
        {Object.entries(data.byProtocol).map(([proto, count]) => (
          <li key={proto}>
            {proto}: <strong style={{ color: 'var(--ac-text)' }}>{count}</strong>
          </li>
        ))}
      </ul>

      <table className="w-full text-sm">
        <thead>
          <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--ac-border)' }}>
            <th scope="col" className="py-2">
              Device
            </th>
            <th scope="col">Protocol</th>
            <th scope="col">Last seen</th>
            <th scope="col">Signal</th>
          </tr>
        </thead>
        <tbody>
          {data.flakiestEntities.map((e) => (
            <tr
              key={e.entityId}
              style={{
                borderBottom: '1px solid var(--ac-border-faint)',
                background: e.needsAttention
                  ? 'var(--ac-warn-bg, rgba(255, 200, 0, 0.06))'
                  : 'transparent',
              }}
            >
              <th scope="row" className="py-2" style={{ fontWeight: 500 }}>
                {e.name}
              </th>
              <td>{e.protocol}</td>
              <td>{silentLabel(e.silentMinutes)}</td>
              <td>{signalLabel(e)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
